import React, { useContext, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import clsx from "clsx";
import i18n from "i18next";
import Cookies from "js-cookie";
import { LinkContext } from "../LinkProvider";

import logo from "../img/logo.svg";

const LANGUAGES = [
  { value: "en", label: "ENG" },
  { value: "ru", label: "RU" },
  { value: "de", label: "DE" },
  { value: "pl", label: "PL" },
  { value: "ro", label: "RO" },
];

function PensadorLogo() {
  return (
    <Link to="/" className="flex items-center shrink-0">
      <motion.img
        src={logo}
        alt="PENSADOR Partner AG"
        className="h-[36px] lg:h-[40px] w-auto"
        whileHover={{ scale: 1.06 }}
        whileTap={{ scale: 0.96 }}
        transition={{ type: "spring", stiffness: 400, damping: 20 }}
      />
    </Link>
  );
}

function Header() {
  const { link } = useContext(LinkContext);
  const { t } = useTranslation();
  const location = useLocation();
  const storedLang = Cookies.get("i18next");
  const [currentLang, setCurrentLang] = useState(storedLang || "en");
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [hidden, setHidden] = useState(false);
  const lastY = useRef(0);
  const langRef = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 20);
      setHidden(y > lastY.current && y > 140);
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  /* Блокируем скролл страницы, пока открыто мобильное меню */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const changeLang = (value) => {
    setCurrentLang(value);
    i18n.changeLanguage(value);
    Cookies.set("i18next", value);
    setLangOpen(false);
  };

  const navLinks = [
    { label: t("header.markets"), to: "/markets" },
    { label: t("header.trading"), to: "/trading" },
    { label: t("header.accounts"), to: "/accounts" },
    { label: t("header.whyUs"), to: "/why-us" },
    { label: t("header.security"), to: "/security" },
  ];

  const currentLabel = LANGUAGES.find(l => l.value === currentLang)?.label || "ENG";

  return (
    <header
      className="fixed top-0 left-0 right-0 z-[100] bg-white"
      style={{
        transform: hidden && !menuOpen ? "translateY(-100%)" : "none",
        boxShadow: scrolled ? "0 2px 20px rgba(28,56,95,0.08)" : "none",
        transition: "transform 0.4s cubic-bezier(0.22,1,0.36,1), box-shadow 0.3s ease",
      }}
    >
      {/* Desktop */}
      <div className="hidden lg:flex items-center justify-between max-w-[1220px] mx-auto px-[40px] h-[70px]">
        <PensadorLogo />

        <nav className="flex items-center gap-[24px]">
          {navLinks.map((item) => {
            const isActive = item.to && location.pathname === item.to;
            return item.to ? (
              <Link
                key={item.label}
                to={item.to}
                className={clsx(
                  "relative font-['Inter'] font-light text-[15px] pb-[4px] transition-colors duration-200",
                  isActive ? "text-[--dark-blue]" : "text-[#2b2b2b] hover:text-[--dark-blue]"
                )}
              >
                {item.label}
                {isActive && (
                  <motion.span
                    layoutId="navUnderline"
                    className="absolute left-0 right-0 bottom-0 h-[2px] rounded-full bg-[--dark-blue]"
                    transition={{ type: "spring", stiffness: 500, damping: 35 }}
                  />
                )}
              </Link>
            ) : (
              <a
                key={item.label}
                href="#"
                className="font-['Inter'] font-light text-[15px] text-[#2b2b2b] hover:text-[--dark-blue] transition-colors duration-200"
              >
                {item.label}
              </a>
            );
          })}

          {/* Language switcher */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => setLangOpen(!langOpen)}
              className="flex items-center gap-[4px] font-['Inter'] font-light text-[15px] text-[#2b2b2b] hover:text-[--dark-blue] transition-colors"
            >
              {currentLabel}
              <ChevronDown className={`w-[14px] h-[14px] transition-transform duration-200 ${langOpen ? "rotate-180" : ""}`} />
            </button>
            {langOpen && (
              <div className="absolute top-[calc(100%+8px)] right-0 bg-white border border-[#eaeaea] rounded-[10px] shadow-lg overflow-hidden z-50 min-w-[100px]">
                {LANGUAGES.map(lang => (
                  <button
                    key={lang.value}
                    onClick={() => changeLang(lang.value)}
                    className={`block w-full text-left px-[16px] py-[9px] font-['Inter'] text-[14px] transition-colors hover:bg-[#f5f5f5] ${currentLang === lang.value ? "text-[#1c385f] font-semibold" : "text-[#2b2b2b]"}`}
                  >
                    {lang.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className="flex items-center gap-[14px]">
          <motion.a
            href={`${link}`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.96 }}
            transition={{ type: "spring", stiffness: 400, damping: 20 }}
            className="flex items-center justify-center h-[42px] px-[28px] rounded-[10px] bg-[#1c385f] font-['Inter'] font-light text-[16px] text-white capitalize hover:opacity-90 transition-opacity"
          >
            {t("header.login")}
          </motion.a>
          <motion.a
            href={`${link}`}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.96 }}
            transition={{ type: "spring", stiffness: 400, damping: 20 }}
            className="flex items-center justify-center h-[42px] px-[28px] rounded-[10px] border border-[#1e1e1e] font-['Inter'] font-light text-[16px] text-[#1e1e1e] capitalize hover:bg-[#f5f5f5] transition-colors"
          >
            {t("header.openAccount")}
          </motion.a>
        </div>
      </div>

      {/* Mobile */}
      <div className="lg:hidden flex items-center justify-between px-[18px] h-[60px]">
        <PensadorLogo />
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="relative w-[40px] h-[40px] -mr-[8px] flex items-center justify-center"
          aria-label="Menu"
        >
          <span
            className={clsx(
              "absolute block w-[24px] h-[2px] bg-[#1c385f] rounded-full transition-all duration-300",
              menuOpen ? "rotate-45" : "-translate-y-[7px]"
            )}
          />
          <span
            className={clsx(
              "absolute block w-[24px] h-[2px] bg-[#1c385f] rounded-full transition-all duration-300",
              menuOpen && "opacity-0 scale-x-0"
            )}
          />
          <span
            className={clsx(
              "absolute block w-[24px] h-[2px] bg-[#1c385f] rounded-full transition-all duration-300",
              menuOpen ? "-rotate-45" : "translate-y-[7px]"
            )}
          />
        </button>
      </div>

      {/* Mobile menu — полноэкранная шторка под шапкой */}
      <div
        className={clsx(
          "lg:hidden absolute top-full left-0 right-0 h-[calc(100dvh-60px)] bg-white flex flex-col overflow-y-auto transition-all duration-300 ease-out",
          menuOpen
            ? "opacity-100 translate-y-0 visible"
            : "opacity-0 -translate-y-[10px] invisible pointer-events-none"
        )}
      >
        <nav className="flex flex-col px-[18px] pt-[6px]">
          {navLinks.map((item, i) => {
            const isActive = item.to && location.pathname === item.to;
            const cls = clsx(
              "flex items-center justify-between py-[16px] border-b border-[#f0f2f5] font-['Inter'] font-semibold text-[19px] transition-all duration-300",
              isActive ? "text-[--accent-blue]" : "text-[#1c385f]",
              menuOpen ? "opacity-100 translate-x-0" : "opacity-0 translate-x-[16px]"
            );
            const style = { transitionDelay: menuOpen ? `${70 + i * 45}ms` : "0ms" };
            return item.to ? (
              <Link
                key={item.label}
                to={item.to}
                onClick={() => setMenuOpen(false)}
                className={cls}
                style={style}
              >
                {item.label}
                <ChevronRight className={clsx("w-[18px] h-[18px] transition-transform duration-300", isActive ? "text-[--accent-blue] translate-x-[3px]" : "text-[#bfccd7]")} />
              </Link>
            ) : (
              <a
                key={item.label}
                href="#"
                onClick={() => setMenuOpen(false)}
                className={cls}
                style={style}
              >
                {item.label}
                <ChevronRight className="w-[18px] h-[18px] text-[#bfccd7]" />
              </a>
            );
          })}
        </nav>

        {/* Переключатель языка */}
        <div
          className={clsx(
            "px-[18px] mt-[24px] transition-all duration-300",
            menuOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[10px]"
          )}
          style={{ transitionDelay: menuOpen ? "320ms" : "0ms" }}
        >
          <p className="font-['Inter'] font-medium text-[12px] uppercase tracking-[1px] text-[#8ba0b8] mb-[12px]">
            {t("header.language")}
          </p>
          <div className="flex flex-wrap gap-[8px]">
            {LANGUAGES.map((lang) => (
              <button
                key={lang.value}
                onClick={() => changeLang(lang.value)}
                className={clsx(
                  "px-[16px] py-[8px] rounded-full border font-['Inter'] text-[13px] transition-colors duration-200",
                  currentLang === lang.value
                    ? "bg-[#1c385f] border-[#1c385f] text-white font-semibold"
                    : "border-[#dfe6ee] text-[#1c385f]"
                )}
              >
                {lang.label}
              </button>
            ))}
          </div>
        </div>

        {/* CTA внизу шторки */}
        <div
          className={clsx(
            "mt-auto px-[18px] pt-[24px] pb-[30px] flex flex-col gap-[10px] transition-all duration-300",
            menuOpen ? "opacity-100 translate-y-0" : "opacity-0 translate-y-[10px]"
          )}
          style={{ transitionDelay: menuOpen ? "380ms" : "0ms" }}
        >
          <a
            href={`${link}`}
            className="flex items-center justify-center h-[50px] rounded-[12px] bg-[#1c385f] font-['Inter'] font-semibold text-[15px] text-white capitalize"
          >
            {t("header.login")}
          </a>
          <a
            href={`${link}`}
            className="flex items-center justify-center h-[50px] rounded-[12px] border border-[#1c385f] font-['Inter'] font-semibold text-[15px] text-[#1c385f] capitalize"
          >
            {t("header.openAccount")}
          </a>
        </div>
      </div>
    </header>
  );
}

export default Header;
